import { tryParseJSON } from '@eventr/common'
import localForage from 'localforage'
import {
    StateStorage,
    createJSONStorage,
    devtools,
    persist,
    subscribeWithSelector,
} from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { DEVTOOLS_ENABLED } from '../consts'
import {
    AccessRequestSlice,
    createAccessRequestSlice,
} from '../feat/account/accessRequestsPage/accessRequest.slice'
import { create } from '../feat/appState/resetState'
import {
    IChatpoolSlice,
    createChatpoolSlice,
} from '../feat/chatpool/chatpool.slice'
import { entryGenerator } from '../feat/local-notifications/schedule-local-notifications'
import { IMediaSlice, createMediaSlice } from '../feat/media/media.slice'
import { OauthSlice, createOauthSlice } from '../feat/oauth/oauth.slice'
import { ProfileSlice, createProfleSlice } from '../feat/profile/profile.slice'
import { AppEventSlice, createAppEventSlice } from './appEvent.slice'
import {
    CalendarSlice,
    createCalendarSlice,
} from './calendarState/calendar.slice'
import { IFieldSlice, createFieldSlice } from './field.slice'
import { CurrentItinSlice } from './itineraryState/currentItin.interfaces'
import { createCurrentItinSlice } from './itineraryState/currentItin.slice'
import {
    ItineraryMapSlice,
    createItineraryMapSlice,
} from './itineraryState/itineraryMap.slice'
import { ListSlice, createListSlice } from './list.slice'
import { LocalNotificationsSlice } from './localNotificationsState/localNotifications.interface'
import { createLocalNotificationsSlice } from './localNotificationsState/localNotifications.slice'
import { OrgConfigSlice, createOrgConfigSlice } from './orgConfig.context'
import { RocketchatSlice } from './rocketchat/rocketchat.interface'
import { createRocketchatSlice } from './rocketchat/rocketchat.slice'
import { ISectionSlice, createSectionSlice } from './section.slice'
import { useStorageStore } from './storage/storage.store'
import { IUserSlice, createUserSlice } from './user.slice'
import {
    VersionConfigSlice,
    createVersionConfigSlice,
} from './versionConfig/versionConfig.slice'
export interface BoundStore
    extends OrgConfigSlice,
        IUserSlice,
        RocketchatSlice,
        ProfileSlice,
        ItineraryMapSlice,
        CalendarSlice,
        ListSlice,
        OauthSlice,
        AppEventSlice,
        AccessRequestSlice,
        ISectionSlice,
        IChatpoolSlice,
        IFieldSlice,
        IMediaSlice,
        CurrentItinSlice,
        LocalNotificationsSlice,
        VersionConfigSlice {}

/// This is to open the store that may or may not exist as a dummy. Ignore but do not delete.
/// Was used to fix deep link indexDB issues on ios. March 2025.
indexedDB.open('keyval-store')
let localForageToUse = localForage.createInstance({
    name: 'keyval-store',
    storeName: 'keyval',
})

const storage: StateStorage = {
    getItem: async (name: string): Promise<string | null> => {
        console.log('Zustand getItem')

        const value = await Promise.race([
            localForageToUse.getItem<string>(name, (error) => {
                console.log('Zustand getItem callback', error)
            }),
            new Promise<string>((resolve) =>
                setTimeout(() => {
                    resolve('FAIL')
                }, 2000)
            ),
        ])

        if (value === 'FAIL') {
            console.log('Zustand getItem value timeoutFail')
        }

        if (value) {
            console.log('Zustand getItem value')
        } else {
            console.log('Zustand getItem value not found')
        }

        return value || null
    },
    setItem: async (name: string, value: string): Promise<void> => {
        if (useStorageStore.getState().hasHydrated !== 'finished') {
            console.log('Zustand setItem fail as not yet hydrated')
            return
        }

        await localForageToUse.setItem(name, value)
    },
    removeItem: async (name: string): Promise<void> => {
        await localForageToUse.removeItem(name)
    },
}

export const useBoundStore = create<BoundStore>()(
    subscribeWithSelector(
        devtools(
            persist(
                (...a) => ({
                    ...createOrgConfigSlice(...a),
                    ...createUserSlice(...a),
                    ...createRocketchatSlice(...a),
                    ...createProfleSlice(...a),
                    ...createItineraryMapSlice(...a),
                    ...createCurrentItinSlice(...a),
                    ...createCalendarSlice(...a),
                    ...createOauthSlice(...a),
                    ...createAppEventSlice(...a),
                    ...createAccessRequestSlice(...a),
                    ...createSectionSlice(...a),
                    ...createChatpoolSlice(...a),
                    ...createFieldSlice(...a),
                    ...createMediaSlice(...a),
                    ...createListSlice(...a),
                    ...createLocalNotificationsSlice(...a),
                    ...createVersionConfigSlice(...a),
                }),
                {
                    name: 'bound-store',
                    migrate: (persistedState: unknown, version) => {
                        console.log('Zustand Finishing Migrate Scripts')
                        return persistedState as BoundStore
                    },
                    version: 1,
                    partialize: (state) =>
                        /// this is to prevent some entries from being persisted
                        /// derived state should not be persisted
                        Object.fromEntries(
                            Object.entries(state).filter(
                                ([key]) =>
                                    ![
                                        'itineraryComponents',
                                        'selectedItinerary',
                                        'credentials',
                                    ].includes(key)
                            )
                        ),
                    storage: createJSONStorage(() => storage),

                    onRehydrateStorage: (state) => {
                        console.log('Zustand Starting Rehydrate Storage')

                        useStorageStore.getState().setHasHydrated('started')

                        const ifBoundStoreInLocal =
                            localStorage.getItem('bound-store')
                        if (ifBoundStoreInLocal) {
                            /// run migrate script...
                            migrateV0ToV1(state)
                        }

                        return (state, error) => {
                            console.log('Zustand done Rehydrate Storage')
                            if (error) {
                                console.error(
                                    'Zustand Error Rehydrate Storage',
                                    error
                                )
                            }
                            useStorageStore
                                .getState()
                                .setHasHydrated('finished')
                        }
                    },
                }
            ),
            {
                name: 'bound-store',
                enabled: DEVTOOLS_ENABLED,
            }
        )
    )
)

// Entry generator used for iOS widgets and local notifications
useBoundStore.subscribe(
    (s) => {
        return {
            itineraryData: s.itineraryData,
            scheduleLocalNotifications: s.scheduleLocalNotifications,
            notificationPermissionsGranted: s.notificationPermissionsGranted,
            notificationFilters: s.notificationFilters,
            itineraryMap: s.itineraryMap,
        }
    },
    entryGenerator,
    {
        equalityFn: shallow,
    }
)

// alias for convenience
export const bs = useBoundStore.getState

/// Purposely using console.log as splog not initialized at this point.
function migrateV0ToV1(currentState: BoundStore) {
    console.log('Zustand Starting Migrate Script V0>V1')
    const accessToken = localStorage.getItem('accessToken') ?? undefined
    const refreshToken = localStorage.getItem('refreshToken') ?? undefined
    const expiration =
        Number.parseInt(localStorage.getItem('expiration') ?? '0') ?? undefined
    const profile = tryParseJSON(
        localStorage.getItem('profile') ?? '{}',
        undefined
    )
    const selectedItin = Number.parseInt(
        localStorage.getItem('selectedItinID') ?? '0'
    )
    const wpUrl = localStorage.getItem('wp_url') ?? undefined

    if (accessToken) {
        currentState.loginCompleted = true
    }
    if (wpUrl) {
        const orgid = wpUrl.split('/').pop()
        if (orgid) {
            currentState.selectedOrg = profile.organisations.find(
                (org: any) => org.name === orgid
            )
        }
    }
    currentState.IDtoken = accessToken ?? ''
    currentState.refreshToken = refreshToken ?? ''
    currentState.expiration = expiration
    currentState.profile = profile
    currentState.selectedItinID = selectedItin

    const itinStore = tryParseJSON(
        localStorage.getItem('itinerary-store') ?? '{}'
    )
    if (itinStore) {
        const map = itinStore.state.itineraryMap
        for (let i = 0; i < map.length; i++) {
            const itin = map[i]
            localStorage.removeItem('ItineraryDataForID:' + itin.id)
        }
    }

    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('expiration')
    localStorage.removeItem('profile')
    localStorage.removeItem('selectedItinID')
    localStorage.removeItem('wp_url')
    localStorage.removeItem('itinerary-store')
    localStorage.removeItem('users')
    localStorage.removeItem('WP_USER')
    localStorage.removeItem('fields')
    localStorage.removeItem('sections')
    localStorage.removeItem('bound-store')

    /// reload data. 1 sec delay for initialization
    setTimeout(() => {
        bs().fetchUsers()
        bs().getProfile()
        bs().forceItineraryMapUpdate()
        bs().fetchOrgConfig()
    }, 1000)

    console.log('Zustand Finishing Migrate Script V0>V1')
}
